import {
  SpanStatusCode,
  type Span as OtelSpan,
} from '@opentelemetry/api';
import type { ISpan, ITracingProvider } from '../../tracing/types';
import type {
  ISpanStatus,
  IStartSpanOptions,
  TSpanAttributes,
  TSpanAttributeValue,
} from '../../types';
import {
  flushOpenTelemetry,
  getOtelTracer,
  shutdownOpenTelemetry,
} from './initOpenTelemetry';

class OtelSpanAdapter implements ISpan {
  constructor(private readonly span: OtelSpan) {}

  setAttribute(key: string, value: TSpanAttributeValue): void {
    this.span.setAttribute(key, value);
  }

  setAttributes(attributes: TSpanAttributes): void {
    this.span.setAttributes(attributes);
  }

  setStatus(status: ISpanStatus): void {
    if (status.code === 'ok') {
      this.span.setStatus({ code: SpanStatusCode.OK, message: status.message });
      return;
    }
    if (status.code === 'error') {
      this.span.setStatus({ code: SpanStatusCode.ERROR, message: status.message });
      return;
    }
    this.span.setStatus({ code: SpanStatusCode.UNSET, message: status.message });
  }

  recordException(error: unknown): void {
    if (error instanceof Error) {
      this.span.recordException(error);
      return;
    }
    this.span.recordException(new Error(String(error)));
  }

  end(): void {
    this.span.end();
  }
}

export class OtelTracingProvider implements ITracingProvider {
  startSpan(name: string, options?: IStartSpanOptions): ISpan {
    const span = getOtelTracer().startSpan(name, {
      attributes: options?.attributes,
    });
    return new OtelSpanAdapter(span);
  }

  async flush(): Promise<void> {
    await flushOpenTelemetry();
  }

  async shutdown(): Promise<void> {
    await shutdownOpenTelemetry();
  }
}
